import dayjs from 'dayjs';

import BannerMessageEntity from 'Models/Entities/BannerMessageEntity';

/**
 * Where the banners are being rendered. Banners always show on the ferry schedule; appearing in the
 * booking wizard is opt-in per banner, so the surface has to be part of the filter.
 */
export type NoticeBannerSurface = 'schedule' | 'bookingWizard';

export interface BannerVisibilityOptions {
	/** The routes in play — a banner shows when its own route set intersects these. */
	routeIds: string[];
	/**
	 * The travel dates the customer is looking at: the schedule page's visible week, or the dates of
	 * the trips they have selected in the wizard. Only used by `MatchTripDates` banners.
	 */
	tripRange?: { start: Date; end: Date };
	surface: NoticeBannerSurface;
	/** Banner ids the customer has dismissed on this device. */
	dismissedIds?: string[];
	/** Injectable for tests; defaults to now. */
	now?: Date;
}

const overlapsTripRange = (banner: BannerMessageEntity, range: { start: Date; end: Date } | undefined): boolean => {
	if (!range) {
		return false;
	}

	const start = banner.startDate ? new Date(banner.startDate) : null;
	const end = banner.endDate ? new Date(banner.endDate) : null;

	// A dated banner with neither bound is about no particular travel dates, so there is nothing to
	// overlap with.
	if (!start && !end) {
		return false;
	}
	if (start && start > range.end) {
		return false;
	}
	if (end && end < range.start) {
		return false;
	}

	return true;
};

const isWithinPublishWindow = (banner: BannerMessageEntity, now: Date): boolean => {
	const today = dayjs(now);

	// Compared at day granularity: the dates are picked as plain dates, so a banner set to publish
	// until the 14th should still be up all day on the 14th.
	if (banner.startDate && today.isBefore(dayjs(banner.startDate).startOf('day'))) {
		return false;
	}
	if (banner.endDate && today.isAfter(dayjs(banner.endDate).endOf('day'))) {
		return false;
	}

	// An open-ended window (no dates at all) is effectively "always", which is what was asked for.
	return true;
};

const isLive = (banner: BannerMessageEntity, options: BannerVisibilityOptions): boolean => {
	switch (banner.displayMode) {
		case 'Always':
			return true;
		case 'DateWindow':
			return isWithinPublishWindow(banner, options.now ?? new Date());
		case 'MatchTripDates':
		default:
			return overlapsTripRange(banner, options.tripRange);
	}
};

/**
 * Whether the customer is allowed to dismiss this banner. Pinned banners never are — they are the
 * ones staff marked as unmissable.
 */
export const canDismiss = (banner: BannerMessageEntity): boolean => banner.dismissible && !banner.pinned;

/**
 * Filters the banners down to the ones that should be shown on this surface right now, then orders
 * them: pinned first, then by descending priority, then by start date so that of two equal banners the
 * one that started earlier leads.
 */
export function getVisibleBanners(
	banners: BannerMessageEntity[],
	options: BannerVisibilityOptions,
): BannerMessageEntity[] {
	const dismissed = new Set(options.dismissedIds ?? []);

	return banners
		.filter(banner => !banner.disabled)
		.filter(banner => options.surface !== 'bookingWizard' || banner.showInBookingWizard)
		// A banner can target several routes (e.g. both directions of a crossing), so it shows when any
		// of its routes is among the routes in play.
		.filter(banner => (banner.routeIds ?? []).some(routeId => options.routeIds.includes(routeId)))
		.filter(banner => isLive(banner, options))
		.filter(banner => !(dismissed.has(banner.id) && canDismiss(banner)))
		.sort((a, b) => {
			if (a.pinned !== b.pinned) {
				return a.pinned ? -1 : 1;
			}
			if (a.priority !== b.priority) {
				return b.priority - a.priority;
			}
			return dayjs(a.startDate ?? 0).valueOf() - dayjs(b.startDate ?? 0).valueOf();
		});
}
